import React, { useState } from 'react';
import { CheckCircle2, Download, Trash2, ExternalLink, Facebook, Linkedin, Edit2, MessageSquare, ChevronDown, ChevronUp, AlertCircle, FileText, Search, User, Send, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PeopleTab } from './PeopleTab';
import { FinancialsTab } from './FinancialsTab';
import { DetailsTab } from './DetailsTab';
import { NotificationsTab } from './NotificationsTab';
import { OverviewTab } from './OverviewTab';
import { DunderMifflinApplicationView } from './DunderMifflinApplicationView';
import { DunderMifflinResearchAgentsTab } from './DunderMifflinResearchAgentsTab';
import { NotesModal } from './NotesModal';
import { ScoutAIButton } from './ScoutAIButton';
const dunderMifflinNotes = [{
  id: '1',
  type: 'user' as const,
  author: 'Dave Davidson',
  timestamp: 'Just now',
  title: 'Large Order Expected',
  content: "Spoke with Michael Scott — they're expecting a large Q2 order from a new client. Will revisit limit increase in 6 months if payment history is clean."
}, {
  id: '2',
  type: 'system' as const,
  timestamp: 'March 27, 2026 · 10:17 AM',
  title: 'Credit Approved with Changes',
  content: 'Credit limit set to $35,000 with 45-day terms.'
}, {
  id: '3',
  type: 'system' as const,
  timestamp: 'March 27, 2026 · 10:15 AM',
  title: 'Application Submitted',
  content: 'Submitted by Pam Beesly on behalf of Dunder Mifflin.'
}];
export function CreditApplication() {
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<null | 'approve' | 'approveWithChanges' | 'reject'>(null);
  const [decisionMade, setDecisionMade] = useState<null | 'approved' | 'approvedWithChanges' | 'rejected'>(null);
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const handleDecision = (decision: 'approved' | 'approvedWithChanges' | 'rejected') => {
    setDecisionMade(decision);
    setActiveModal(null);
  };
  return <div className="min-h-full bg-gray-50 p-6" data-id="element-556" data-mp="cwbdz808e2hg">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-557" data-mp="cxmhd6prl881">
        {/* Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-558" data-mp="humolkycoh0q">
          <div className="flex items-center justify-between gap-4" data-id="element-559" data-mp="15l8im8bis0rq">
            {/* Left: Company Info + Notes */}
            <div className="shrink-0" data-id="element-560" data-mp="19kr2r1vio7ti">
              <h1 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-561" data-mp="vuduszimkt46">
                DUNDER MIFFLIN PAPER COMPANY
              </h1>
              <div className="flex items-center gap-3 text-indigo-600 mb-2" data-id="element-562" data-mp="10rxliywieyd0">
                <ExternalLink className="w-3.5 h-3.5 cursor-pointer" data-id="element-563" data-mp="1oijhvsx5iu76" />
                <Facebook className="w-3.5 h-3.5 cursor-pointer" data-id="element-564" data-mp="1t9jd8tqmh47k" />
                <Linkedin className="w-3.5 h-3.5 cursor-pointer" data-id="element-565" data-mp="1taui6wvt0i09" />
                <Edit2 className="w-3.5 h-3.5 cursor-pointer" data-id="element-566" data-mp="22njwtverlozr" />
              </div>
              <div className="space-y-0.5 text-xs text-gray-600 mb-2" data-id="element-567" data-mp="1yn9o4azl8kh7">
                <div className="flex items-center gap-1.5" data-id="element-568" data-mp="1ykx6y01nme57">
                  <span className="font-semibold text-gray-900" data-id="element-569" data-mp="2cctoja5p55nc">
                    Credit Manager:
                  </span>
                  <span data-id="element-570" data-mp="27h4tnk4mgcxd">Sarah Chen</span>
                  <Edit2 className="w-3 h-3 text-indigo-600 cursor-pointer" data-id="element-571" data-mp="1joxru5g4ndqk" />
                </div>
                <div className="flex items-center gap-1.5" data-id="element-572" data-mp="1fodt4xfwo22b">
                  <span className="font-semibold text-gray-900" data-id="element-573" data-mp="1fpynalyb5izn">Added:</span>
                  <span data-id="element-574" data-mp="1tp851q25wl5q">3/27/2026</span>
                </div>
              </div>
              <button onClick={() => setShowNotesModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded flex items-center gap-1.5 text-xs font-medium relative" data-id="element-575" data-mp="1onivyfyi2hka">
                <MessageSquare className="w-3 h-3" data-id="element-576" data-mp="3063dqmsb1p5b" />
                NOTES
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-id="element-577" data-mp="19o6m1osxtj72">
                  {dunderMifflinNotes.length}
                </span>
              </button>
            </div>

            {/* Right: Risk Gauge */}
            <div className="flex flex-col items-center shrink-0 mr-4" data-id="element-578" data-mp="1nnps8c26ukgq">
              <div className="flex items-end gap-2 mb-1" data-id="element-579" data-mp="1skg8azdztg25">
                <span className="text-xs text-gray-400 font-medium" data-id="element-580" data-mp="1xkkmujvcc0pq">0</span>
                <div className="relative w-64" data-id="element-581" data-mp="1xihmb5wkap6r">
                  {/* Score label */}
                  <div className="absolute -top-6 left-[82%] -translate-x-1/2 bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded" data-id="element-582" data-mp="22c2ola56u5br">
                    82
                  </div>
                  {/* Bar */}
                  <div className="relative w-full h-6 flex rounded-full overflow-hidden" data-id="element-583" data-mp="2ba4lpelswdtr">
                    <div className="w-1/3 bg-red-300" data-id="element-584" data-mp="2g8zaqyad2ymb"></div>
                    <div className="w-1/3 bg-yellow-300" data-id="element-585" data-mp="4orw9skcbore"></div>
                    <div className="w-1/3 bg-green-300" data-id="element-586" data-mp="3vu9q6mmhby02"></div>
                  </div>
                  {/* Indicator line */}
                  <div className="absolute top-0 bottom-0 left-[82%] w-0.5 flex flex-col items-center" data-id="element-587" data-mp="3rtg1vs54kh6n">
                    <div className="w-[3px] h-full bg-gray-700 rounded-full" style={{
                    borderLeft: '2px dashed #374151'
                  }} data-id="element-588" data-mp="3mud6z0iuzrce"></div>
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-medium" data-id="element-589" data-mp="el386hg0qclb">100</span>
              </div>
              <div className="w-64 flex justify-between px-0 mt-1" data-id="element-590" data-mp="9nbebhvtcgeh">
                <span className="text-xs text-red-500 font-medium pl-7" data-id="element-591" data-mp="9kyyba9m8g42">
                  Low
                </span>
                <span className="text-xs text-yellow-600 font-medium" data-id="element-592" data-mp="12bqqzxrh2mmn">
                  Medium
                </span>
                <span className="text-xs text-gray-600 font-medium pr-7" data-id="element-593" data-mp="1l7fyzk0eymgh">
                  High
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-200 mt-4 pt-3" data-id="element-594" data-mp="1cg778s8v65qz">
            {['OVERVIEW', 'NOTIFICATIONS', 'DETAILS', 'FINANCIALS', 'PEOPLE', 'RESEARCH AGENTS', 'APPLICATIONS'].map(tab => <motion.button key={tab} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => {
            setActiveTab(tab);
            setDecisionOpen(false);
          }} className={`flex-1 text-center py-1.5 text-xs font-semibold transition-colors ${tab === activeTab ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-595" data-mp="17hcd4u5g4urk">
                {tab}
              </motion.button>)}
          </div>
        </div>

        {/* Credit Decision Section */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm overflow-hidden" data-id="element-596" data-mp="pznobv79zb8u">
          {/* Header Row */}
          <button onClick={() => setDecisionOpen(!decisionOpen)} className="w-full p-4 flex items-center justify-between bg-indigo-100 hover:bg-indigo-150 transition-colors" data-id="element-597" data-mp="l6kudlyl5zs4">
            <div className="flex items-center gap-4" data-id="element-598" data-mp="uztdn4q2olnr">
              <span className="font-bold text-gray-900" data-id="element-599" data-mp="18wf6u2eztmbu">Credit Decision</span>
              <span className="border border-green-500 text-green-600 px-2.5 py-0.5 rounded-full text-sm font-medium" data-id="element-600" data-mp="13xvjpyony4hw">
                Ready
              </span>
              <span className="text-sm text-gray-500" data-id="element-601" data-mp="3aqxod78el3mq">
                Date: March 27, 2026
              </span>
              <span className="text-sm text-gray-300" data-id="element-602" data-mp="2ku2d20lpihz3">·</span>
              <span className="text-sm font-medium text-gray-700" data-id="element-603" data-mp="2ktkid8ep0bta">
                Recommended Credit Limit: $50,000
              </span>
            </div>
            {decisionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-604" data-mp="2tnp63iyj574l" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-605" data-mp="2ylqmtx8hgucw" />}
          </button>

          {/* Expanded Content */}
          {decisionOpen && <div className="p-6 border-t border-indigo-200" data-id="element-606" data-mp="21xkqdxlpt89p">
              <div className="grid grid-cols-2 gap-x-12 gap-y-6 mb-8" data-id="element-607" data-mp="25xl39dt61ben">
                {/* Left Column */}
                <div className="space-y-4" data-id="element-608" data-mp="2aubjc14z0702">
                  <div data-id="element-609" data-mp="2fuprrkahhiym">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-610" data-mp="2fv96yt8r1t8y">
                      Recommended Decision
                    </p>
                    <p className="text-xl font-bold text-gray-900" data-id="element-611" data-mp="3majqygz9e8il">
                      Approve for $50,000
                    </p>
                  </div>
                  <div data-id="element-612" data-mp="1fnc8etcdil12">
                    <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-613" data-mp="1flho9h8xewy1">
                      Terms
                    </p>
                    <div className="text-sm text-gray-600 space-y-0.5" data-id="element-614" data-mp="1apsww1728mct">
                      <p data-id="element-615" data-mp="15von7a6eu081">Payment Terms Days: 30</p>
                      <p data-id="element-616" data-mp="10xwkrm852vpl">Discount: 5%</p>
                      <p data-id="element-617" data-mp="10wv6l8vttt3n">Discount Days: 10</p>
                      <p data-id="element-618" data-mp="10w28sxeuidxn">Late Pay Penalty: 1.5%</p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4" data-id="element-619" data-mp="rwf9ch5ofi78">
                  <div data-id="element-620" data-mp="my5btzwhc5ig">
                    <div className="flex items-center gap-2 mb-1" data-id="element-621" data-mp="mxltii2x3f3w">
                      <p className="text-sm text-gray-500" data-id="element-622" data-mp="mzyihf73epqg">Policy Applied</p>
                      <span className="text-sm text-gray-300" data-id="element-623" data-mp="i0vcnsjagfhf">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-624" data-mp="2hb3lchacuy4q">
                        (View Policy)
                      </button>
                    </div>
                    <p className="font-bold text-gray-900" data-id="element-625" data-mp="vd3f5wjlhn9a">
                      My Company's Credit Policy v1
                    </p>
                  </div>

                  <div data-id="element-626" data-mp="qh5whjkv04tc">
                    <div className="flex items-center gap-2 mb-1" data-id="element-627" data-mp="15clycarol50a">
                      <p className="text-sm text-gray-500" data-id="element-628" data-mp="159q0emfu2gbu">
                        Credit Application
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-629" data-mp="cmvgprqubkzf">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-630" data-mp="cl28i80kwspb">
                        (View Application)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-631" data-mp="mfavyhar5vgm">
                      Submitted on: March 27, 2026
                    </p>
                  </div>

                  <div data-id="element-632" data-mp="3q19p9iq9s9tu">
                    <div className="flex items-center gap-2 mb-1" data-id="element-633" data-mp="3q0gqb3xjyom9">
                      <p className="text-sm text-gray-500" data-id="element-634" data-mp="3l25g2s3cic3i">
                        Recommendation Summary
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-635" data-mp="20juxp1pocq2v">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-636" data-mp="25cxqh7mmnvi0">
                        (Full Reasoning)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-637" data-mp="1vn4hmedvduhg">
                      Dunder Mifflin Paper Company, Inc. is approved for credit
                      based on strong business fundamentals, an established
                      77-year operating history, and positive trade references.
                      However, the credit limit is conservatively set due to the
                      applicant's limited initial order size and the need to
                      establish a direct payment history, warranting a measured
                      initial credit exposure.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-4 gap-4" data-id="element-638" data-mp="35jjemov1yxpt">
                <button onClick={() => setActiveModal('approve')} className="bg-green-700 hover:bg-green-800 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-639" data-mp="30l9edzuh16xc">
                  APPROVE
                </button>
                <button onClick={() => setActiveModal('approveWithChanges')} className="bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-640" data-mp="2qt3px03fh7mw">
                  APPROVE WITH CHANGES
                </button>
                <button onClick={() => setActiveModal('reject')} className="bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-641" data-mp="2cvnipmdb59u3">
                  REJECT
                </button>
                <button className="border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-3 rounded-lg font-semibold text-sm" data-id="element-642" data-mp="64piqxzajdql">
                  REGENERATE RECOMMENDATION
                </button>
              </div>
            </div>}
        </div>

        {/* Decision Modals */}
        <AnimatePresence data-id="element-643" data-mp="ne04wmup98f9">
          {activeModal && <>
              {/* Backdrop */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-black/50 z-50" data-id="element-644" data-mp="scbd6t5cjavd" />

              {/* Modal */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-645" data-mp="9nwq2gy5ggzu">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6" data-id="element-646" data-mp="2p28hu6ibtr3v">
                  {activeModal === 'approve' && <>
                      <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-647" data-mp="2xzsnrsr4wbkt">
                        Approve Recommendation
                      </h2>
                      <div className="space-y-4" data-id="element-648" data-mp="37yrjybupqeqx">
                        <div className="grid grid-cols-3 gap-3" data-id="element-649" data-mp="37xyinqeiwhg6">
                          <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2 col-span-2" data-id="element-650" data-mp="3lqo864t9g4i1">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-651" data-mp="3hob5jp9t8mph">
                              Credit Limit
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-sm text-gray-900 outline-none" data-id="element-652" data-mp="3vlzpmuw1zpth" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-653" data-mp="j3ynp4mgvbd9">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-654" data-mp="46xwr3c3nkfu">
                              Currency
                            </legend>
                            <select className="w-full text-sm text-gray-900 outline-none bg-transparent" data-id="element-655" data-mp="3rk05sf7h05q7">
                              <option data-id="element-656" data-mp="6dtrj4uhmls">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-657" data-mp="3hk9a4a76bkoj">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-658" data-mp="3mlq8nz26qv1y">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-659" data-mp="3cniufnza6r3n" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-3" data-id="element-660" data-mp="1ec0ja2lsm3jq">
                          <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-661" data-mp="19fa3zhhtbbzd">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-662" data-mp="14h8nn4brtqrl">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-663" data-mp="1x4l18ozyeo8r" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-664" data-mp="1o5zj2uye9b8i">
                            <legend className="text-xs text-gray-500 px-1" data-id="element-665" data-mp="gnyx9odff181">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-666" data-mp="2ocuuvt4e68iu" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-667" data-mp="3249oetvxds45">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-668" data-mp="3719moimj9qkp">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-sm text-gray-900 outline-none" data-id="element-669" data-mp="3g2znug67w3vo" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-2 pt-1 pb-2" data-id="element-670" data-mp="1lqj90qzcvhf7">
                          <legend className="text-xs text-gray-500 px-1" data-id="element-671" data-mp="1qoumdbov0a26">
                            Reasoning
                          </legend>
                          <textarea rows={3} defaultValue="Approved based on strong financials and positive trade references." className="w-full text-sm text-gray-900 outline-none resize-none" data-id="element-672" data-mp="3ojzymkezvn1i" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-3 mt-6" data-id="element-673" data-mp="39n0x9eaqkqfh">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-4 py-2 text-sm hover:bg-indigo-50 rounded" data-id="element-674" data-mp="35m79ckxanbml">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approved')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-6 py-2 text-sm rounded" data-id="element-675" data-mp="ytmy818u0clz">
                          CONFIRM APPROVAL
                        </button>
                      </div>
                    </>}

                  {activeModal === 'approveWithChanges' && <>
                      <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-676" data-mp="27r4o68172och">
                        Approve and Input Values
                      </h2>
                      <div className="space-y-6" data-id="element-677" data-mp="2vjbotjdydhhp">
                        <div className="grid grid-cols-3 gap-4" data-id="element-678" data-mp="2zj29r6gst3eb">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-679" data-mp="2lrfd35iy1sv0">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-680" data-mp="2qqigklj9zr0v">
                              Credit Limit *
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-681" data-mp="3eeiblxnme3l9" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-682" data-mp="88y259nv3dj0">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-683" data-mp="3qmxky77wvx5g">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-684" data-mp="3un7pdfg22fi7">
                              <option data-id="element-685" data-mp="3low29needzui">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-686" data-mp="37qfstop5hv8o">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-687" data-mp="3brhjvi8wz0zk">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-688" data-mp="19zczqsxnp7ze" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-689" data-mp="2qhn2ivchscre">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-690" data-mp="3488ncpliawrx">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-691" data-mp="2zg8e26jjqrio">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-692" data-mp="3e77ntkw4bm3u" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-693" data-mp="3i7hqooom5weh">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-694" data-mp="1ssjyfsooznoh">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-695" data-mp="3nvq3dvvndjzg" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-696" data-mp="5q3dmmonc5hb">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-697" data-mp="aoy7qks2dggq">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-698" data-mp="flwugi9eel4w" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-699" data-mp="tfxqh48ytu1k">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-700" data-mp="ydheuwzdnn78">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-701" data-mp="2iu6zypzcnc6b" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-702" data-mp="24xa8qnfbjsow">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-703" data-mp="31mrndrwa4p2q">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approvedWithChanges')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-704" data-mp="2nve9oul1ylfm">
                          CONFIRM APPROVAL
                        </button>
                      </div>
                    </>}

                  {activeModal === 'reject' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-705" data-mp="354xcpeizqpef">
                        Reject
                      </h2>
                      <div className="space-y-6" data-id="element-706" data-mp="2w2o1emfwerzz">
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-707" data-mp="2w3hahu2bxtla">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-708" data-mp="3jt1wklts56ys">
                            Reasoning
                          </legend>
                          <textarea rows={6} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-709" data-mp="3a2qja8o7hlf8" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-710" data-mp="6kfcvnjmov7r">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-711" data-mp="bcymyaj3x87t">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('rejected')} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded" data-id="element-712" data-mp="uhv72fl5m0hj">
                          CONFIRM REJECTION
                        </button>
                      </div>
                    </>}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {activeTab === 'OVERVIEW' ? <OverviewTab data-id="element-713" data-mp="6qps02n1fc8a" /> : activeTab === 'FINANCIALS' ? <FinancialsTab data-id="element-714" data-mp="1n5qz36zs347z" /> : activeTab === 'PEOPLE' ? <PeopleTab data-id="element-715" data-mp="1n83o243yeeuj" /> : activeTab === 'DETAILS' ? <DetailsTab data-id="element-716" data-mp="1i5uw9b2thiu0" /> : activeTab === 'NOTIFICATIONS' ? <NotificationsTab data-id="element-717" data-mp="1q7vo3vjpc2ma" /> : activeTab === 'RESEARCH AGENTS' ? <DunderMifflinResearchAgentsTab data-id="element-718" data-mp="1q9yierrw0hwt" /> : activeTab === 'APPLICATIONS' ? <DunderMifflinApplicationView data-id="element-719" data-mp="1uba5apjdmp1b" /> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center text-gray-500" data-id="element-720" data-mp="1z9bsnmnu5ail">
            Content for {activeTab} tab coming soon.
          </div>}
      </div>

      <NotesModal isOpen={showNotesModal} onClose={() => setShowNotesModal(false)} notes={dunderMifflinNotes} companyName="DUNDER MIFFLIN PAPER COMPANY" data-id="element-721" data-mp="16ko33ew1zyz8" />

      <ScoutAIButton companyName="dunder" data-id="element-722" data-mp="1ggrcn5t8izw7" />
    </div>;
}